import React, { useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { FormGroup, Input, Label } from 'reactstrap';

import apiFiles from 'api/files';
import apiPdfEnd, {
  ExtraPageType,
  IOfferExtraPage,
  OfferOwner,
} from 'api/offerPdfEnd';
import LinkButton from 'components/layout/buttons/LinkButton';
import ModalConfirm from 'components/layout/modals/ModalConfirm';
import PrimaryButton from 'components/layout/buttons/PrimaryButton';
import translations from 'services/translations/translations';
import { finishLoading, startLoading } from 'store/loading/actions';
import { addMessage } from 'store/messages/actions';

import RichOfferTextEditor from '../offerProduct/calculationWindow/RichOfferTextEditor';
import CollapsibleOfferSection from './CollapsibleOfferSection';

import 'style/offers/OfferExtraPages.css';

const IMAGE_TYPES = ['image/jpeg', 'image/png'];
const PDF_TYPE = 'application/pdf';
const MAX_TITLE_LENGTH = 200;
const PAGE_TYPES: ExtraPageType[] = ['text', 'image', 'pdf'];

// A page being edited; a new page has no id until it is saved
interface IDraft {
  fileId: null | string;
  fileName: null | string;
  id: null | number;
  // A file chosen but not yet saved
  newFile: File | null;
  text: string;
  title: string;
  type: ExtraPageType;
}

const toDraft = (page: IOfferExtraPage): IDraft => ({
  fileId: page.fileId,
  fileName: page.fileName,
  id: page.id,
  newFile: null,
  text: page.text ?? '',
  title: page.title ?? '',
  type: page.type,
});

const emptyDraft = (): IDraft => ({
  fileId: null,
  fileName: null,
  id: null,
  newFile: null,
  text: '',
  title: '',
  type: 'text',
});

/**
 * One extra page: the title the user writes and the content, a text, an image filling the
 * page or an attached PDF whose pages are printed as they are.
 */
const ExtraPageEditor = ({
  draft,
  locked,
  onCancelNew,
  onDelete,
  onMove,
  onSave,
  owner,
  ownerId,
  position,
  pageCount,
}: {
  draft: IDraft;
  locked: boolean;
  onCancelNew: () => void;
  onDelete: () => void;
  onMove: (direction: -1 | 1) => void;
  onSave: (draft: IDraft) => Promise<void>;
  owner: OfferOwner;
  ownerId: number;
  pageCount: number;
  position: number;
}): JSX.Element => {
  const dispatch = useDispatch();
  const { t } = useTranslation([translations.offers, translations.common]);
  const fileInput = useRef<HTMLInputElement>(null);
  const [edited, setEdited] = useState<IDraft>(draft);
  const [imageUrl, setImageUrl] = useState<string>();
  const [dragActive, setDragActive] = useState(false);
  const [saving, setSaving] = useState(false);
  const tr = (key: string, options?: any) =>
    t(`${translations.offers}:extraPages.${key}`, options);

  useEffect(() => setEdited(draft), [draft]);

  // The saved image is fetched with authorization; a chosen image is shown from the file
  useEffect(() => {
    let url: string | undefined;
    let cancelled = false;

    const show = async () => {
      if (edited.type !== 'image') {
        setImageUrl(undefined);
        return;
      }

      if (edited.newFile !== null) {
        url = URL.createObjectURL(edited.newFile);
        setImageUrl(url);
        return;
      }

      if (edited.id === null || edited.fileId === null) {
        setImageUrl(undefined);
        return;
      }

      try {
        const response = await apiPdfEnd.extraPages.getFile(
          owner,
          ownerId,
          edited.id
        );
        if (!cancelled) {
          url = URL.createObjectURL(
            new Blob([response.data], {
              type: response.headers['content-type'],
            })
          );
          setImageUrl(url);
        }
      } catch (error) {
        setImageUrl(undefined);
      }
    };

    show();

    return () => {
      cancelled = true;
      if (url !== undefined) {
        URL.revokeObjectURL(url);
      }
    };
  }, [edited.type, edited.newFile, edited.fileId, edited.id]);

  const acceptedTypes = edited.type === 'pdf' ? [PDF_TYPE] : IMAGE_TYPES;

  const chooseFile = (file: File | undefined) => {
    if (file === undefined) {
      return;
    }

    if (!acceptedTypes.includes(file.type)) {
      dispatch(
        addMessage(tr(edited.type === 'pdf' ? 'Only PDF' : 'Only images'))
      );
      return;
    }

    setEdited({ ...edited, fileName: file.name, newFile: file });
  };

  // A file of the other kind does not go with the new type
  const changeType = (type: ExtraPageType) =>
    setEdited({
      ...edited,
      fileId: null,
      fileName: null,
      newFile: null,
      type,
    });

  const fileMissing =
    edited.type !== 'text' && edited.newFile === null && edited.fileId === null;

  const save = async () => {
    if (saving || fileMissing) {
      return;
    }

    setSaving(true);
    try {
      await onSave(edited);
    } finally {
      setSaving(false);
    }
  };

  const openPdf = async () => {
    if (edited.newFile !== null) {
      window.open(URL.createObjectURL(edited.newFile), '_blank');
      return;
    }

    if (edited.id === null) {
      return;
    }

    dispatch(startLoading('offer-extra-page-file'));
    try {
      const response = await apiPdfEnd.extraPages.getFile(
        owner,
        ownerId,
        edited.id
      );
      window.open(
        URL.createObjectURL(new Blob([response.data], { type: PDF_TYPE })),
        '_blank'
      );
    } catch (error) {
      dispatch(addMessage(tr('File failed')));
    } finally {
      dispatch(finishLoading('offer-extra-page-file'));
    }
  };

  return (
    <div className="offer-extra-page">
      <div className="offer-extra-page-fields">
        <FormGroup className="offer-extra-page-title">
          <Label for={`extra-page-title-${edited.id ?? 'new'}`}>
            {tr('Title')}
          </Label>
          <Input
            disabled={locked}
            id={`extra-page-title-${edited.id ?? 'new'}`}
            maxLength={MAX_TITLE_LENGTH}
            onChange={(event) =>
              setEdited({ ...edited, title: event.target.value })
            }
            value={edited.title}
          />
        </FormGroup>
        <FormGroup tag="fieldset" className="offer-extra-page-types">
          <legend className="col-form-label">{tr('Content')}</legend>
          {PAGE_TYPES.map((type) => (
            <Label check key={type} className="offer-extra-page-type">
              <Input
                aria-label={tr(`types.${type}`)}
                checked={edited.type === type}
                disabled={locked}
                name={`extra-page-type-${edited.id ?? 'new'}`}
                onChange={() => changeType(type)}
                type="radio"
              />{' '}
              {tr(`types.${type}`)}
            </Label>
          ))}
        </FormGroup>
      </div>

      {edited.type === 'text' ? (
        <RichOfferTextEditor
          // A new editor for each page, as the editor reads its value when created
          key={`extra-page-text-${edited.id ?? 'new'}`}
          disabled={locked}
          label=""
          onChange={(text) => setEdited((current) => ({ ...current, text }))}
          tall
          value={edited.text}
          wide
        />
      ) : (
        <div
          className={`offer-extra-page-drop${dragActive ? ' active' : ''}`}
          onDragLeave={() => setDragActive(false)}
          onDragOver={(event) => {
            if (!locked) {
              event.preventDefault();
              setDragActive(true);
            }
          }}
          onDrop={(event) => {
            event.preventDefault();
            setDragActive(false);
            if (!locked) {
              chooseFile(event.dataTransfer.files[0]);
            }
          }}
        >
          {edited.type === 'image' && imageUrl !== undefined ? (
            <img alt="" className="offer-extra-page-image" src={imageUrl} />
          ) : null}
          {edited.type === 'pdf' && edited.fileName !== null ? (
            <LinkButton onClick={openPdf}>{edited.fileName}</LinkButton>
          ) : null}
          {edited.fileName === null ? (
            <p className="offer-extra-page-hint">
              {tr(edited.type === 'pdf' ? 'Drop PDF' : 'Drop image')}
            </p>
          ) : null}
          {locked ? null : (
            <>
              <input
                accept={acceptedTypes.join(',')}
                hidden
                onChange={(event) => {
                  chooseFile(event.target.files?.[0]);
                  // eslint-disable-next-line no-param-reassign
                  event.target.value = '';
                }}
                ref={fileInput}
                type="file"
              />
              <LinkButton onClick={() => fileInput.current?.click()}>
                {tr(edited.fileName === null ? 'Choose file' : 'Change file')}
              </LinkButton>
            </>
          )}
          {edited.type === 'pdf' ? (
            <p className="offer-extra-page-hint">{tr('Pdf info')}</p>
          ) : null}
        </div>
      )}

      {locked ? null : (
        <div className="offer-extra-page-actions">
          {edited.id === null ? (
            <LinkButton onClick={onCancelNew}>
              {t(`${translations.common}:Cancel`)}
            </LinkButton>
          ) : (
            <>
              {position > 0 ? (
                <LinkButton onClick={() => onMove(-1)}>
                  {tr('Move up')}
                </LinkButton>
              ) : null}
              {position < pageCount - 1 ? (
                <LinkButton onClick={() => onMove(1)}>
                  {tr('Move down')}
                </LinkButton>
              ) : null}
              <LinkButton onClick={onDelete}>{tr('Delete')}</LinkButton>
            </>
          )}
          <PrimaryButton
            className="button-one-line"
            disabled={saving || fileMissing}
            onClick={save}
          >
            {tr('Save').toLocaleUpperCase()}
          </PrimaryButton>
        </div>
      )}
    </div>
  );
};

/**
 * The extra pages after the delivery terms, each a section of its own with the title the
 * user has written. Each is printed on a page of its own in the offer PDF.
 */
const OfferExtraPages = ({
  locked,
  owner,
  ownerId,
}: {
  locked: boolean;
  owner: OfferOwner;
  ownerId: number;
}): JSX.Element => {
  const dispatch = useDispatch();
  const { t } = useTranslation([translations.offers, translations.common]);
  const [pages, setPages] = useState<IOfferExtraPage[]>([]);
  const [openIds, setOpenIds] = useState<(number | 'new')[]>([]);
  const [newDraft, setNewDraft] = useState<IDraft | null>(null);
  const [pageToDelete, setPageToDelete] = useState<IOfferExtraPage>();
  const tr = (key: string, options?: any) =>
    t(`${translations.offers}:extraPages.${key}`, options);

  const load = async () => {
    try {
      setPages(await apiPdfEnd.extraPages.get(owner, ownerId));
    } catch (error) {
      setPages([]);
    }
  };

  useEffect(() => {
    load();
  }, [owner, ownerId]);

  const toggle = (id: number | 'new') =>
    setOpenIds((current) =>
      current.includes(id)
        ? current.filter((openId) => openId !== id)
        : [...current, id]
    );

  const save = async (draft: IDraft) => {
    dispatch(startLoading('offer-extra-page'));
    try {
      const fileId =
        draft.newFile === null
          ? draft.fileId
          : `${(await apiFiles.post(draft.newFile, null)).id}`;

      const result = await apiPdfEnd.extraPages.save(owner, ownerId, draft.id, {
        fileId: draft.type === 'text' ? null : fileId,
        text: draft.type === 'text' ? draft.text : null,
        title: draft.title,
        type: draft.type,
      });

      if (draft.id === null) {
        setNewDraft(null);
        setOpenIds((current) => [
          ...current.filter((id) => id !== 'new'),
          Number(result.id),
        ]);
      }

      await load();
      dispatch(addMessage(tr('Saved')));
    } catch (error) {
      dispatch(addMessage(tr('Save failed')));
    } finally {
      dispatch(finishLoading('offer-extra-page'));
    }
  };

  const move = async (index: number, direction: -1 | 1) => {
    const ids = pages.map((page) => page.id);
    const target = index + direction;
    [ids[index], ids[target]] = [ids[target], ids[index]];

    try {
      await apiPdfEnd.extraPages.putOrder(owner, ownerId, ids);
      await load();
    } catch (error) {
      dispatch(addMessage(tr('Save failed')));
    }
  };

  const remove = async () => {
    if (pageToDelete === undefined) {
      return;
    }

    const page = pageToDelete;
    setPageToDelete(undefined);

    try {
      await apiPdfEnd.extraPages.delete(owner, ownerId, page.id);
      await load();
      dispatch(addMessage(tr('Deleted')));
    } catch (error) {
      dispatch(addMessage(tr('Save failed')));
    }
  };

  const titleOf = (title: null | string) =>
    title !== null && title.trim() !== '' ? title : tr('Untitled');

  return (
    <>
      <ModalConfirm
        showing={pageToDelete !== undefined}
        setShowing={() => setPageToDelete(undefined)}
        header={tr('Delete')}
        text={tr('Delete confirmation', {
          title: titleOf(pageToDelete?.title ?? null),
        })}
        yesButton={t(`${translations.common}:Confirm`)}
        noButton={t(`${translations.common}:Cancel`)}
        confirm={remove}
      />
      {pages.map((page, index) => (
        <React.Fragment key={page.id}>
          <hr />
          <CollapsibleOfferSection
            isOpen={openIds.includes(page.id)}
            title={titleOf(page.title)}
            toggle={() => toggle(page.id)}
          >
            <ExtraPageEditor
              draft={toDraft(page)}
              locked={locked}
              onCancelNew={() => undefined}
              onDelete={() => setPageToDelete(page)}
              onMove={(direction) => move(index, direction)}
              onSave={save}
              owner={owner}
              ownerId={ownerId}
              pageCount={pages.length}
              position={index}
            />
          </CollapsibleOfferSection>
        </React.Fragment>
      ))}
      {newDraft === null ? null : (
        <>
          <hr />
          <CollapsibleOfferSection
            isOpen={openIds.includes('new')}
            title={tr('New page')}
            toggle={() => toggle('new')}
          >
            <ExtraPageEditor
              draft={newDraft}
              locked={locked}
              onCancelNew={() => setNewDraft(null)}
              onDelete={() => undefined}
              onMove={() => undefined}
              onSave={save}
              owner={owner}
              ownerId={ownerId}
              pageCount={pages.length}
              position={pages.length}
            />
          </CollapsibleOfferSection>
        </>
      )}
      {locked || newDraft !== null ? null : (
        <div className="offer-extra-pages-add">
          <LinkButton
            onClick={() => {
              setNewDraft(emptyDraft());
              setOpenIds((current) => [...current, 'new']);
            }}
          >
            {`+ ${tr('Add page')}`}
          </LinkButton>
        </div>
      )}
    </>
  );
};

export default OfferExtraPages;
